import { useEffect, useMemo, useRef, useState } from "react";

function playLateAlert(audioEl) {
    if (!audioEl) return;
    try {
        audioEl.pause?.();
        audioEl.currentTime = 0;
        const p = audioEl.play?.();
        if (p && typeof p.catch === "function") {
            p.catch(() => {});
        }
    } catch (e) {
        // ignore autoplay/unsupported
    }
}

export function useKitchenLateAlerts({
    normalizedOrders,
    pendingOrders,
    lastFoodReadyAtByOrderName,
    thresholdMinutes,
    foodLateTimeEnabled,
    isOrderLate,
    getOrderCreatedAt,
}) {
    const prevLateByOrderNameRef = useRef(null);
    const lateAudioRef = useRef(null);
    const lastLateSoundAtRef = useRef(0);
    const [flashByOrderName, setFlashByOrderName] = useState(() => ({}));
    const [nowTs, setNowTs] = useState(() => Date.now());

    useEffect(() => {
        const a = new Audio("/assets/sounds/kds_late_alert.wav");
        a.preload = "auto";
        a.volume = 0.35;
        lateAudioRef.current = a;
        return () => {
            try {
                a.pause?.();
            } catch (e) {
                // ignore
            }
        };
    }, []);

    const lateByOrderName = useMemo(() => {
        const m = new Map();
        for (const o of normalizedOrders) {
            if (!o?.name) continue;
            m.set(
                o.name,
                isOrderLate({
                    order: o,
                    lastFoodReadyAt: lastFoodReadyAtByOrderName.get(o.name),
                    thresholdMinutes,
                    enabled: foodLateTimeEnabled,
                    nowMs: nowTs,
                })
            );
        }
        return m;
    }, [
        normalizedOrders,
        lastFoodReadyAtByOrderName,
        thresholdMinutes,
        foodLateTimeEnabled,
        nowTs,
        isOrderLate,
    ]);

    useEffect(() => {
        if (!foodLateTimeEnabled) return;
        if (!thresholdMinutes || thresholdMinutes <= 0) return;
        const thresholdMs = thresholdMinutes * 60_000;

        const now = Date.now();
        let nextLateAtMs = null;

        for (const order of pendingOrders) {
            if (!order?.name || order.food_ready_time) continue;
            const isLateNow = lateByOrderName.get(order.name);
            if (isLateNow) continue;
            const createdAt = getOrderCreatedAt(order);
            const refAt = lastFoodReadyAtByOrderName.get(order.name) || createdAt;
            if (!refAt) continue;
            const lateAt = refAt.getTime() + thresholdMs;
            if (lateAt <= now) {
                setNowTs(now);
                return;
            }
            nextLateAtMs =
                nextLateAtMs == null ? lateAt : Math.min(nextLateAtMs, lateAt);
        }

        if (nextLateAtMs == null) return;
        const delayMs = Math.max(0, nextLateAtMs - now);
        const id = setTimeout(() => {
            setNowTs(Date.now());
        }, delayMs + 150);
        return () => clearTimeout(id);
    }, [
        pendingOrders,
        lateByOrderName,
        lastFoodReadyAtByOrderName,
        thresholdMinutes,
        foodLateTimeEnabled,
        getOrderCreatedAt,
    ]);

    useEffect(() => {
        const prev = prevLateByOrderNameRef.current;
        if (!prev) {
            prevLateByOrderNameRef.current = new Map(lateByOrderName);
            return;
        }

        const transitioned = [];
        for (const order of pendingOrders) {
            const name = order?.name;
            if (!name) continue;
            const was = prev.get(name);
            const nowLate = lateByOrderName.get(name);
            if (was === false && nowLate === true) {
                transitioned.push(name);
            }
        }

        prevLateByOrderNameRef.current = new Map(lateByOrderName);
        if (transitioned.length > 0) {
            transitioned.forEach((name) => {
                setFlashByOrderName((prevFlash) => ({
                    ...prevFlash,
                    [name]: true,
                }));
                setTimeout(() => {
                    setFlashByOrderName((prevFlash) => {
                        if (!prevFlash?.[name]) return prevFlash;
                        const next = { ...prevFlash };
                        delete next[name];
                        return next;
                    });
                }, 3000);
            });

            const THROTTLE_MS = 1000;
            const now = Date.now();
            if (now - (lastLateSoundAtRef.current || 0) >= THROTTLE_MS) {
                lastLateSoundAtRef.current = now;
                playLateAlert(lateAudioRef.current);
            }
        }
    }, [pendingOrders, lateByOrderName]);

    return {
        lateByOrderName,
        flashByOrderName,
        nowTs,
    };
}
